import { CreateUserRequest } from '../types';
import { apiClient } from '../common/utils/apiClient';
import { API_CONFIG } from '../config/api';
import { sessionStorageUtils } from '../common/utils/sessionStorage';

// API User interface matching the backend response
export interface ApiUser {
  tblid: string;
  userid: string;
  firstname: string;
  lastname: string;
  username: string;
  mobilenumber: string;
  email: string;
  role: string;
  isactive: string;
  cretedby: string;
  createddt: string | null;
  modifiedby: string;
  modifieddt: string | null;
}

// Real API users service
export const usersService = {
  // Get all users (matches API: GET /users)
  getUsers: async (): Promise<{ data: ApiUser[]; rowCount?: number }> => {
    try {
      const token = sessionStorageUtils.getToken();
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await apiClient.get<any>(
        API_CONFIG.ENDPOINTS.USERS,
        token
      );
      // Normalize response
      if (Array.isArray(response)) {
        return { data: response, rowCount: response.length };
      }
      if (response && Array.isArray(response.data)) {
        return { data: response.data as ApiUser[], rowCount: response.rowCount };
      }
      return { data: [], rowCount: 0 };
    } catch (error) {
      throw new Error('Failed to fetch users');
    }
  },

  // Get user by ID (matches API: GET /users/userid/{userid})
  getUserById: async (id: string): Promise<ApiUser> => {
    try {
      const token = sessionStorageUtils.getToken();
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await apiClient.get<ApiUser>(
        API_CONFIG.ENDPOINTS.USER_BY_ID(id),
        token
      );
      
      return response;
    } catch (error) {
      throw new Error('Failed to fetch user');
    }
  },

  // Get user by userid (matches API: GET /users/userid/{userid})
  getUserByUserId: async (userid: string): Promise<ApiUser> => {
    try {
      const token = sessionStorageUtils.getToken();
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await apiClient.get<ApiUser>(
        API_CONFIG.ENDPOINTS.USER_BY_ID(userid),
        token
      );
      
      return response;
    } catch (error) {
      throw new Error('Failed to fetch user');
    }
  },

  // Create user (matches API: POST /users)
  createUser: async (userData: CreateUserRequest): Promise<ApiUser> => {
    try {
      const token = sessionStorageUtils.getToken();
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await apiClient.post<ApiUser>(
        API_CONFIG.ENDPOINTS.USERS,
        userData,
        token
      );
      
      return response;
    } catch (error) {
      throw new Error('Failed to create user');
    }
  },

  // Update user by ID (legacy method for backward compatibility)
  updateUser: async (id: string, userData: Partial<CreateUserRequest>): Promise<ApiUser> => {
    try {
      const token = sessionStorageUtils.getToken();
      if (!token) {
        throw new Error('No authentication token');
      }

      // For legacy compatibility, we'll use the userid field if available
      const userid = userData.userid || id;
      const response = await apiClient.put<ApiUser>(
        API_CONFIG.ENDPOINTS.USER_BY_ID(userid),
        userData,
        token
      );
      
      return response;
    } catch (error) {
      throw new Error('Failed to update user');
    }
  },

  // Update user by userid (matches API: PUT /users/userid/{userid})
  updateUserByUserId: async (userid: string, userData: Partial<CreateUserRequest>): Promise<ApiUser> => {
    try {
      const token = sessionStorageUtils.getToken();
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await apiClient.put<ApiUser>(
        API_CONFIG.ENDPOINTS.USER_BY_ID(userid),
        userData,
        token
      );
      
      return response;
    } catch (error) {
      throw new Error('Failed to update user');
    }
  },

  // Delete user (soft delete by setting isactive to 'N')
  deleteUser: async (id: string, modifiedBy?: string): Promise<void> => {
    try {
      const token = sessionStorageUtils.getToken();
      if (!token) {
        throw new Error('No authentication token');
      }

      // First get the user to get their userid
      const user = await usersService.getUserById(id);
      const userid = user.userid;

      // Update user to set isactive to 'N'
      await apiClient.put(
        API_CONFIG.ENDPOINTS.USER_BY_ID(userid),
        { 
          isactive: 'N',
          modifiedby: modifiedBy || 'SYSTEM'
        },
        token
      );
    } catch (error) {
      throw new Error('Failed to delete user');
    }
  },

  // Toggle user status (activate/deactivate)
  toggleUserStatus: async (id: string, modifiedBy?: string): Promise<ApiUser> => {
    try {
      const token = sessionStorageUtils.getToken();
      if (!token) {
        throw new Error('No authentication token');
      }

      // First get the user to get their current status and userid
      const user = await usersService.getUserById(id);
      const userid = user.userid;
      const newStatus = user.isactive === 'Y' ? 'N' : 'Y';

      const response = await apiClient.put<ApiUser>(
        API_CONFIG.ENDPOINTS.USER_BY_ID(userid),
        { 
          isactive: newStatus,
          modifiedby: modifiedBy || 'SYSTEM'
        },
        token
      );
      
      return response;
    } catch (error) {
      throw new Error('Failed to toggle user status');
    }
  },

  // Change password
  changePassword: async (id: string, oldPassword: string, newPassword: string): Promise<void> => {
    try {
      const token = sessionStorageUtils.getToken();
      if (!token) {
        throw new Error('No authentication token');
      }

      // First get the user to get their userid
      const user = await usersService.getUserById(id);
      const userid = user.userid;

      await apiClient.put(
        API_CONFIG.ENDPOINTS.USER_BY_ID(userid),
        { 
          password: newPassword,
          oldPassword: oldPassword 
        },
        token
      );
    } catch (error) {
      throw new Error('Failed to change password');
    }
  },

  // Reset password
  resetPassword: async (_email: string): Promise<void> => {
    try {
      const token = sessionStorageUtils.getToken();
      if (!token) {
        throw new Error('No authentication token');
      }

      // This would typically be a separate endpoint for password reset
      // For now, we'll throw an error as this endpoint might not exist
      throw new Error('Password reset functionality not implemented');
    } catch (error) {
      throw new Error('Failed to reset password');
    }
  },

  // Get user statistics (calculated from the users list)
  getUserStats: async () => {
    try {
      const { data: users } = await usersService.getUsers();
      
      const totalUsers = users.length;
      const activeUsers = users.filter(u => u.isactive === 'Y').length;
      const disabledUsers = users.filter(u => u.isactive === 'N').length;

      // Calculate breakdown by roles using API field names
      const getRoleBreakdown = (users: ApiUser[]) => {
        return {
          admin: users.filter(u => u.role === 'admin').length,
          packer: users.filter(u => u.role === 'packer').length,
          receiver: users.filter(u => u.role === 'receiver').length,
          security: users.filter(u => u.role === 'security').length,
          user: users.filter(u => u.role === 'user').length,
        };
      };

      return {
        total: {
          count: totalUsers,
          breakdown: getRoleBreakdown(users)
        },
        active: {
          count: activeUsers,
          breakdown: getRoleBreakdown(users.filter(u => u.isactive === 'Y'))
        },
        disabled: {
          count: disabledUsers,
          breakdown: getRoleBreakdown(users.filter(u => u.isactive === 'N'))
        }
      };
    } catch (error) {
      throw new Error('Failed to get user statistics');
    }
  },
};